import React from 'react';
import { Paper, Typography } from '@material-ui/core/';

/**
 * The `metrics` result format — a flat map of scalars.
 *
 *     metrics: { name: number | string, … }        plus an optional `metrics_note`
 *
 * **Flat, not a list of tile descriptors**, because `supervised-risk` already returned exactly this shape and
 * satisfied the contract with no change to its Python at all. That is the test of a good contract here: the
 * algorithms that were already reporting numbers sensibly should not have to be rewritten to be drawn.
 *
 * `x_mean` and `x_std` are paired into one tile — reporting a mean without its spread is how a PR-AUC of 0.42
 * across folds that ranged 0.11 to 0.79 gets mistaken for a result.
 */

const isNumber = (v) => typeof v === 'number' && isFinite(v);

const prettify = (key) => String(key)
    .replace(/_/g, ' ')
    .replace(/\bauc\b/gi, 'AUC')
    .replace(/\bpr\b/gi, 'PR')
    .replace(/\broc\b/gi, 'ROC')
    .replace(/\boof\b/gi, 'OOF')
    .replace(/^([a-z])/, (m, c) => c.toUpperCase());

const format = (v) => {
    if (v === null || v === undefined) return '—';
    if (!isNumber(v)) return String(v);
    if (Number.isInteger(v)) return v.toLocaleString();
    // Fractions between 0 and 1 read better with more places than a count does.
    return Math.abs(v) < 1 ? v.toFixed(4) : v.toFixed(3);
};

/** Fold `x_mean` / `x_std` into one tile, leaving everything else alone. */
function tilesFrom(metrics) {
    const keys = Object.keys(metrics);
    const consumed = new Set();
    const tiles = [];

    for (const key of keys) {
        if (consumed.has(key)) continue;
        if (key.endsWith('_mean')) {
            const stem = key.slice(0, -'_mean'.length);
            const stdKey = keys.includes(`${stem}_std`) ? `${stem}_std` : null;
            if (stdKey) consumed.add(stdKey);
            consumed.add(key);
            tiles.push({
                label: prettify(stem),
                value: format(metrics[key]),
                sub: stdKey ? `± ${format(metrics[stdKey])}` : null
            });
            continue;
        }
        if (key.endsWith('_std') && keys.includes(`${key.slice(0, -'_std'.length)}_mean`)) continue;
        consumed.add(key);
        tiles.push({ label: prettify(key), value: format(metrics[key]), sub: null });
    }
    return tiles;
}

export default function MetricsPanel({ data }) {
    const metrics = (data && data.metrics && typeof data.metrics === 'object' && !Array.isArray(data.metrics))
        ? data.metrics
        : null;

    if (!metrics || !Object.keys(metrics).length) {
        return <Typography variant="body2" style={{ padding: 20, opacity: 0.7 }}>
            This result has no <code>metrics</code> block.
        </Typography>;
    }

    const tiles = tilesFrom(metrics);

    return <div>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {tiles.map((t, i) => <Paper key={i} style={{ margin: 5, padding: '10px 16px', minWidth: 170 }}>
                <Typography variant="caption" style={{ opacity: 0.75 }}>{t.label}</Typography>
                <Typography variant="h5">{t.value}</Typography>
                {t.sub ? <Typography variant="caption" style={{ opacity: 0.75 }}>{t.sub}</Typography> : null}
            </Paper>)}
        </div>
        {data.metrics_note
            ? <Typography variant="caption" style={{ display: 'block', margin: 10, opacity: 0.75 }}>
                {data.metrics_note}
            </Typography>
            : null}
        {Array.isArray(data.warnings) && data.warnings.length
            ? <Paper style={{ margin: 5, padding: 10 }}>
                <Typography variant="subtitle2">Warnings</Typography>
                {data.warnings.map((w, i) => <Typography key={i} variant="body2">• {w}</Typography>)}
            </Paper>
            : null}
    </div>;
}
